/**
 * F09 v2 — `/verifications/direct` (super_admin, `verifications:issue_privileged`).
 *
 * Grant the tick without a request: pick the entity type + id (or find it by
 * owner user id), add a required internal note (10–500) and optional ID
 * fields. Prerequisites are still checked (409 `PREREQUISITE_NOT_MET` lists
 * them); a 409 `DUPLICATE_IDENTITY` offers an acknowledge-and-verify retry.
 * That acknowledgement is tied to the entity and ID inputs the matches were
 * computed for: changing the entity type / id (typed or picked) or any ID
 * field drops it, so the next submit re-runs the duplicate check.
 * Pre-filled from `?entityType=&entityId=` (candidates / entity page / the
 * legacy `/users/:id/verify` redirect).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Card,
  FormField,
  Select,
  Spinner,
  TextInput,
  Textarea,
  useToast,
} from '@bravemove/ui-web';
import {
  VERIFIABLE_ENTITY_TYPES,
  VERIFICATION_INTERNAL_NOTE_MAX,
  VERIFICATION_INTERNAL_NOTE_MIN,
  type AdminDirectVerifyInput,
  type AdminEntityVerificationProfile,
  type EntityVerificationSummary,
  type VerifiableEntityType,
  type VerificationDuplicateMatch,
} from '@bravemove/types';
import { adminVerificationApi } from '../../api/verification';
import { PageHeader } from '../../components/PageHeader';
import { DuplicateMatchList } from '../../components/verification/ApproveDialog';
import { lengthError, noErrors } from '../../components/verification/validation';
import { IdDocumentFields } from '../../components/verification/IdDocumentFields';
import {
  COUNTRY_RE,
  EMPTY_ID_DOCUMENT,
  type IdDocumentErrors,
  type IdDocumentValues,
} from '../../components/verification/idDocument';
import { entityLabel, entityPath } from '../../components/verification/labels';
import { VerificationErrorBanner } from '../../components/verification/VerificationBits';
import {
  duplicateMatchesOf,
  verificationErrorCode,
} from '../../components/verification/verificationErrors';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isEntityType(v: string | null): v is VerifiableEntityType {
  return v !== null && (VERIFIABLE_ENTITY_TYPES as readonly string[]).includes(v);
}

interface Lookup {
  readonly key: string;
  readonly profile: AdminEntityVerificationProfile | null;
  readonly error: unknown;
}

/** The duplicate matches the server returned, and the exact inputs they were computed for. */
interface DuplicateCheck {
  readonly matches: readonly VerificationDuplicateMatch[];
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
  /** Identity-compared: every edit produces a new values object. */
  readonly forIdDoc: IdDocumentValues;
}

export function VerificationDirectVerifyPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('entityType');
  const initialId = searchParams.get('entityId') ?? '';

  const [entityType, setEntityType] = useState<VerifiableEntityType>(
    isEntityType(initialType) ? initialType : 'player',
  );
  const [entityId, setEntityId] = useState(initialId);
  const [lookupKey, setLookupKey] = useState<string | null>(
    isEntityType(initialType) && UUID_RE.test(initialId) ? `${initialType}:${initialId}` : null,
  );
  const [lookup, setLookup] = useState<Lookup | null>(null);

  const [ownerUserId, setOwnerUserId] = useState('');
  const [ownerResults, setOwnerResults] = useState<readonly EntityVerificationSummary[] | null>(
    null,
  );
  const [ownerError, setOwnerError] = useState<unknown>(null);

  const [internalNote, setInternalNote] = useState('');
  const [idDoc, setIdDoc] = useState<IdDocumentValues>(EMPTY_ID_DOCUMENT);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [idErrors, setIdErrors] = useState<IdDocumentErrors>({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [duplicateCheck, setDuplicateCheck] = useState<DuplicateCheck | null>(null);
  const [busy, setBusy] = useState(false);

  // The acknowledgement is only offered (and only sent) while the form still
  // points at the entity + ID inputs the shown matches were computed for. This
  // also covers a 409 that lands after the admin edited the form mid-request.
  const duplicates =
    duplicateCheck !== null &&
    duplicateCheck.entityType === entityType &&
    duplicateCheck.entityId === entityId.trim() &&
    duplicateCheck.forIdDoc === idDoc
      ? duplicateCheck.matches
      : null;

  const changeEntityType = (next: VerifiableEntityType) => {
    setEntityType(next);
    setDuplicateCheck(null);
  };
  const changeEntityId = (next: string) => {
    setEntityId(next);
    setDuplicateCheck(null);
  };
  const changeIdDoc = (next: IdDocumentValues) => {
    setIdDoc(next);
    setDuplicateCheck(null);
  };

  useEffect(() => {
    if (!lookupKey) return undefined;
    let cancelled = false;
    const [type, id] = lookupKey.split(':') as [VerifiableEntityType, string];
    adminVerificationApi.entityProfile(type, id).then(
      (profile) => {
        if (!cancelled) setLookup({ key: lookupKey, profile, error: null });
      },
      (error: unknown) => {
        if (!cancelled) setLookup({ key: lookupKey, profile: null, error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [lookupKey]);

  const currentKey = `${entityType}:${entityId.trim()}`;
  // A lookup result only counts while the form still points at that entity.
  const current = lookup !== null && lookup.key === currentKey ? lookup : null;
  const preview = current?.profile ?? null;
  const lookupError = current?.error ?? null;
  const lookupLoading = lookupKey === currentKey && lookup?.key !== lookupKey;

  const runLookup = () => {
    if (!UUID_RE.test(entityId.trim())) {
      setErrors((e) => ({ ...e, entityId: t('admin:verification.validation.uuid') }));
      return;
    }
    setErrors((e) => ({ ...e, entityId: undefined }));
    setLookupKey(currentKey);
  };

  const searchOwner = async () => {
    if (!UUID_RE.test(ownerUserId.trim())) {
      setErrors((e) => ({ ...e, ownerUserId: t('admin:verification.validation.uuid') }));
      return;
    }
    setErrors((e) => ({ ...e, ownerUserId: undefined }));
    setOwnerError(null);
    try {
      setOwnerResults(await adminVerificationApi.entitiesForOwner(ownerUserId.trim()));
    } catch (err) {
      setOwnerError(err);
      setOwnerResults(null);
    }
  };

  const pick = (s: EntityVerificationSummary) => {
    changeEntityType(s.entityType);
    changeEntityId(s.entityId);
    setLookupKey(`${s.entityType}:${s.entityId}`);
  };

  const submit = async (acknowledgeDuplicate: boolean) => {
    const submitted = { entityType, entityId: entityId.trim(), forIdDoc: idDoc };
    const acknowledge = acknowledgeDuplicate && duplicates !== null;
    const anyId = idDoc.number.trim().length > 0 || idDoc.type !== '';
    const nextErrors = {
      entityId: UUID_RE.test(entityId.trim()) ? undefined : t('admin:verification.validation.uuid'),
      internalNote: lengthError(
        t,
        internalNote,
        VERIFICATION_INTERNAL_NOTE_MIN,
        VERIFICATION_INTERNAL_NOTE_MAX,
        true,
      ),
    };
    const nextId: IdDocumentErrors = anyId
      ? {
          number:
            idDoc.number.trim().length === 0
              ? t('admin:verification.validation.idFieldsTogether')
              : undefined,
          type: idDoc.type === '' ? t('admin:verification.validation.idFieldsTogether') : undefined,
          country: COUNTRY_RE.test(idDoc.country)
            ? undefined
            : t('admin:verification.validation.country'),
        }
      : {};
    setErrors(nextErrors);
    setIdErrors(nextId);
    if (!noErrors({ ...nextErrors, ...nextId })) return;

    const body: AdminDirectVerifyInput = {
      internalNote: internalNote.trim(),
      ...(anyId && idDoc.type !== ''
        ? {
            idDocumentNumber: idDoc.number.trim(),
            idDocumentType: idDoc.type,
            idDocumentIssuingCountry: idDoc.country,
          }
        : {}),
      ...(acknowledge ? { acknowledgeDuplicate: true } : {}),
    };
    setBusy(true);
    setSubmitError(null);
    try {
      await adminVerificationApi.directVerify(submitted.entityType, submitted.entityId, body);
      setIdDoc(EMPTY_ID_DOCUMENT);
      setDuplicateCheck(null);
      toast.push({ tone: 'success', title: t('admin:verification.toast.directVerified') });
      navigate(entityPath(submitted.entityType, submitted.entityId));
    } catch (err) {
      if (verificationErrorCode(err) === 'DUPLICATE_IDENTITY') {
        setDuplicateCheck({ ...submitted, matches: duplicateMatchesOf(err) });
      } else {
        setSubmitError(err);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="verification-direct-page">
      <PageHeader
        title={t('admin:verification.pageTitle.direct')}
        subtitle={t('admin:verification.direct.subtitle')}
        actions={
          <Button size="sm" variant="ghost" icon="back" onClick={() => navigate('/verifications')}>
            {t('admin:verification.common.back')}
          </Button>
        }
      />

      <Card>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[12rem_1fr_auto] sm:items-end">
            <FormField label={t('admin:verification.direct.entityType')} required>
              <Select
                value={entityType}
                options={VERIFIABLE_ENTITY_TYPES.map((v) => ({
                  value: v,
                  label: entityLabel(t, v),
                }))}
                data-testid="direct-entity-type"
                onChange={(e) => {
                  if (isEntityType(e.target.value)) changeEntityType(e.target.value);
                }}
              />
            </FormField>
            <FormField
              label={t('admin:verification.direct.entityId')}
              helper={t('admin:verification.direct.entityIdHelper')}
              error={errors.entityId}
              required
            >
              <TextInput
                value={entityId}
                autoComplete="off"
                invalid={!!errors.entityId}
                data-testid="direct-entity-id"
                onChange={(e) => changeEntityId(e.target.value)}
              />
            </FormField>
            <Button variant="secondary" onClick={runLookup} data-testid="direct-lookup">
              {t('admin:verification.direct.lookup')}
            </Button>
          </div>

          {lookupLoading ? <Spinner size={20} /> : null}
          {lookupError ? (
            <VerificationErrorBanner error={lookupError} testId="direct-lookup-error" />
          ) : null}
          {preview ? (
            <div data-testid="direct-preview">
              <Banner tone={preview.verificationStatus === 'verified' ? 'warning' : 'info'}>
                {t('admin:verification.direct.preview', {
                  name: preview.entityName,
                  state: t(`verification:badge.state.${preview.verificationState}`),
                })}
                {preview.verificationStatus === 'verified'
                  ? ` — ${t('admin:verification.direct.alreadyVerified')}`
                  : ''}
              </Banner>
            </div>
          ) : null}

          <details className="rounded-md border border-hairline px-3 py-2">
            <summary className="cursor-pointer text-[13px] font-semibold text-ink-2">
              {t('admin:verification.direct.ownerSearch')}
            </summary>
            <div className="mt-3 space-y-2">
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                <FormField
                  label={t('admin:verification.direct.ownerUserId')}
                  error={errors.ownerUserId}
                >
                  <TextInput
                    value={ownerUserId}
                    autoComplete="off"
                    invalid={!!errors.ownerUserId}
                    data-testid="direct-owner-id"
                    onChange={(e) => setOwnerUserId(e.target.value)}
                  />
                </FormField>
                <Button
                  variant="secondary"
                  data-testid="direct-owner-search"
                  onClick={() => void searchOwner()}
                >
                  {t('admin:verification.direct.ownerSearchButton')}
                </Button>
              </div>
              {ownerError ? (
                <VerificationErrorBanner error={ownerError} testId="direct-owner-error" />
              ) : null}
              {ownerResults !== null && ownerResults.length === 0 ? (
                <p className="text-[13px] text-ink-3">
                  {t('admin:verification.direct.ownerSearchEmpty')}
                </p>
              ) : null}
              {ownerResults && ownerResults.length > 0 ? (
                <ul className="space-y-1.5" data-testid="direct-owner-results">
                  {ownerResults.map((s) => (
                    <li
                      key={`${s.entityType}:${s.entityId}`}
                      className="flex items-center justify-between gap-2 rounded-md border border-hairline px-3 py-2 text-[13px]"
                    >
                      <span>
                        <span className="font-semibold text-ink">{s.entityName}</span>
                        <span className="text-ink-3"> · {entityLabel(t, s.entityType)}</span>
                      </span>
                      <Button size="xs" variant="ghost" onClick={() => pick(s)}>
                        {t('admin:verification.direct.pick')}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </details>

          <FormField
            label={t('admin:verification.direct.internalNote')}
            helper={t('admin:verification.direct.internalNoteHelper')}
            error={errors.internalNote}
            required
          >
            <Textarea
              value={internalNote}
              maxLength={VERIFICATION_INTERNAL_NOTE_MAX}
              invalid={!!errors.internalNote}
              data-testid="direct-internal-note"
              onChange={(e) => setInternalNote(e.target.value)}
            />
          </FormField>

          <fieldset className="space-y-2">
            <legend className="text-[12px] font-semibold text-ink-2">
              {t('admin:verification.direct.idSection')}
            </legend>
            <IdDocumentFields
              values={idDoc}
              onChange={changeIdDoc}
              errors={idErrors}
              required={false}
              testIdPrefix="direct"
            />
          </fieldset>

          {submitError ? (
            <VerificationErrorBanner error={submitError} testId="direct-error" />
          ) : null}

          {duplicates ? (
            <div className="space-y-2" data-testid="direct-duplicates">
              <Banner tone="warning" title={t('admin:verification.approve.duplicateTitle')}>
                {t('admin:verification.approve.duplicateBody')}
              </Banner>
              <DuplicateMatchList matches={duplicates} testId="direct-duplicate-matches" />
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            {duplicates ? (
              <Button
                variant="destructive"
                loading={busy}
                data-testid="direct-acknowledge"
                onClick={() => void submit(true)}
              >
                {t('admin:verification.approve.acknowledgeAndApprove')}
              </Button>
            ) : (
              <Button
                variant="primary"
                icon="shield"
                loading={busy}
                data-testid="direct-submit"
                onClick={() => void submit(false)}
              >
                {t('admin:verification.direct.confirm')}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
